import React from 'react';
import { Grid } from 'lucide-react';
import KundliChart from './KundliChart';
import SectionHeader from './SectionHeader';
import { chartMapping } from './utils';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../../languages/freeKundli';

interface ChartsTabProps {
    kundliData: any;
    language: 'english' | 'hindi';
}

const ChartsTab: React.FC<ChartsTabProps> = ({ kundliData, language }) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[langKey];

    // Transform planets for KundliChart
    const transformPlanets = (planetsData: any) => {
        if (!planetsData || Object.keys(planetsData).length === 0) return [];

        return Object.entries(planetsData).map(([name, data]: [string, any]) => {
            const p = data;
            return {
                planet: name,
                house: p.house || p['house-num'] || p.house_no || 1,
                rashi: p.sign || p.rashi || '',
                longitude: typeof p.degree === 'number' ? p.degree.toFixed(2) : p.degree,
                nakshatra: typeof p.nakshatra === 'object' ? p.nakshatra.name : p.nakshatra,
                pada: typeof p.nakshatra === 'object' ? p.nakshatra.pada : p.pada
            };
        });
    };

    // Get planets for a specific chart
    const getPlanetsForChart = (chartKey: string) => {
        if (chartKey === 'D1') {
            return kundliData.planets || kundliData.D1?.planets || {};
        }
        return kundliData[chartKey]?.planets || {};
    };

    // Chart descriptions
    const chartDescriptions: Record<string, { en: string; hi: string }> = {
        D1: { en: 'Birth Chart - Core personality and life path', hi: 'जन्म कुंडली - मूल व्यक्तित्व और जीवन पथ' },
        D2: { en: 'Wealth and Family possessions', hi: 'धन और पारिवारिक संपत्ति' },
        D3: { en: 'Siblings, Courage and Short journeys', hi: 'भाई-बहन, साहस और छोटी यात्राएं' },
        D4: { en: 'Property, Fortune and Mother', hi: 'संपत्ति, भाग्य और माता' },
        D7: { en: 'Children and Progeny matters', hi: 'संतान और वंश संबंधित' },
        D9: { en: 'Marriage, Fortune and Dharma', hi: 'विवाह, भाग्य और धर्म' },
        D10: { en: 'Career, Profession and Status', hi: 'करियर, पेशा और प्रतिष्ठा' },
        D12: { en: 'Parents and Lineage', hi: 'माता-पिता और वंश' },
        D16: { en: 'Vehicles, Comforts and Luxuries', hi: 'वाहन, सुख और विलासिता' },
        D20: { en: 'Spiritual practices and Worship', hi: 'आध्यात्मिक साधना और पूजा' },
        D24: { en: 'Education and Learning', hi: 'शिक्षा और ज्ञान' },
        D27: { en: 'Physical strength and Vitality', hi: 'शारीरिक शक्ति और जीवन शक्ति' },
        D30: { en: 'Misfortunes and Difficulties', hi: 'दुर्भाग्य और कठिनाइयां' },
        D40: { en: 'Auspicious/Inauspicious effects', hi: 'शुभ/अशुभ प्रभाव' },
        D45: { en: 'General well-being', hi: 'सामान्य कल्याण' },
        D60: { en: 'Past life karma and Effects', hi: 'पूर्व जन्म कर्म और प्रभाव' }
    };

    // All available charts
    const availableCharts = Object.keys(chartMapping);

    return (
        <div className="animate-in fade-in duration-500">
            {/* All Charts in Single Card */}
            <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                <SectionHeader
                    icon={Grid}
                    title={t.allCharts || "All Divisional Charts"}
                    description={t.allChartsDesc || "Complete collection of Varga charts (D1 to D60) for detailed analysis."}
                />

                {/* Charts Grid - Inside the card */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-6">
                    {availableCharts.map((chartKey) => {
                    const planets = getPlanetsForChart(chartKey);
                    const hasData = Object.keys(planets).length > 0;
                    const chartName = t.chartNames?.[chartKey] || chartMapping[chartKey] || chartKey;
                    const description = chartDescriptions[chartKey]?.[language === 'hindi' ? 'hi' : 'en'] || '';

                    return (
                        <div
                            key={chartKey}
                            className={`bg-white/[0.03] border border-white/10 rounded-[20px] md:rounded-[24px] overflow-hidden transition-all hover:bg-white/[0.06] hover:border-white/20 ${!hasData ? 'opacity-50' : ''}`}
                        >
                            {/* Chart Header */}
                            <div className="p-4 border-b border-white/10">
                                <div className="flex items-center justify-between mb-1">
                                    <h3 className="font-bold text-white text-sm md:text-base">{chartName}</h3>
                                    <span className="text-xs font-mono text-primary-400 bg-primary-500/20 px-2 py-0.5 rounded">{chartKey}</span>
                                </div>
                                <p className="text-[10px] md:text-xs text-white/50 line-clamp-1">{description}</p>
                            </div>

                            {/* Chart Content */}
                            <div className="p-2">
                                {hasData ? (
                                    <KundliChart
                                        key={chartKey}
                                        language={language}
                                        planets={transformPlanets(planets)}
                                    />
                                ) : (
                                    <div className="aspect-square flex items-center justify-center bg-white/[0.02] rounded-xl">
                                        <div className="text-center">
                                            <Grid size={24} className="text-white/20 mx-auto mb-2" />
                                            <p className="text-xs text-white/30">{t.noData || "No data available"}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                </div>
            </div>
        </div>
    );
};

export default ChartsTab;
